import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { MapPin, User, Truck, Plus } from '@phosphor-icons/react';
import SupplierLocationPicker from '../../components/SupplierLocationPicker';
import SupplierSearchSelect from '../../components/SupplierSearchSelect';

const PRIORITIES = [
  { value: 'shortage', label: 'Shortage (Urgent)' },
  { value: 'qr', label: 'QR' },
  { value: 'sample', label: 'Sample' },
  { value: 'others', label: 'Others' },
];

export default function NewBooking() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [pilots, setPilots] = useState([]);
  // Supplier location picker dialog state — { side: 'pickup'|'drop', supplier }
  const [locDialog, setLocDialog] = useState(null);
  const [form, setForm] = useState({
    vehicle_type_1: '', vehicle_type_2: '',
    pickup_supplier_name: '', pickup_location: '', pickup_lat: 0, pickup_lng: 0, pickup_contact: '',
    drop_supplier_name: '', drop_location: '', drop_lat: 0, drop_lng: 0,
    item: '', qty: 0, uom: '', priority: 'others',
    material_ready_datetime: '', remarks: '', preferred_pilot_name: ''
  });
  const [loading, setLoading] = useState(false);
  const [pilotPickDialog, setPilotPickDialog] = useState(null); // { tripId, pilots }
  const [availPilotsLoading, setAvailPilotsLoading] = useState(false);
  const [canSelectPreferred, setCanSelectPreferred] = useState(false);
  const [pickupDialog, setPickupDialog] = useState(null);       // { candidates:[] }
  const [selectedPickupUser, setSelectedPickupUser] = useState(null); // {id, name, mobile}

  useEffect(() => {
    Promise.all([
      api.get('/booking/suppliers'),
      api.get('/booking/items'),
      api.get('/booking/uom'),
      api.get('/booking/vehicles-types'),
      api.get('/booking/pilots'),
      api.get('/booking/me'),
    ]).then(([s, i, u, v, p, me]) => {
      setSuppliers(s.data);
      setItems(i.data);
      setUoms(u.data);
      setVehicleTypes(v.data);
      setPilots(p.data);
      setCanSelectPreferred(!!me.data?.can_select_preferred_pilot);
    }).catch(() => {});
  }, []);

  const refreshSuppliers = async () => {
    try { const r = await api.get('/booking/suppliers'); setSuppliers(r.data); } catch {}
  };

  const handleLocationSaved = (updated) => {
    refreshSuppliers();
    // If the saved supplier is the one currently selected on either side,
    // patch its lat/lng into the form immediately.
    setForm(f => {
      const next = { ...f };
      if (updated.name === f.pickup_supplier_name) {
        next.pickup_lat = updated.lat;
        next.pickup_lng = updated.lng;
        next.pickup_location = updated.address || f.pickup_location;
      }
      if (updated.name === f.drop_supplier_name) {
        next.drop_lat = updated.lat;
        next.drop_lng = updated.lng;
        next.drop_location = updated.address || f.drop_location;
      }
      return next;
    });
  };

  const handlePickupSupplier = (supplier) => {
    // Phase 67: Only supplier objects from the master list are accepted.
    // Clearing passes null. Free-text entry is impossible.
    setForm(f => ({
      ...f,
      pickup_supplier_name: supplier?.name || '',
      pickup_location: supplier?.address || '',
      pickup_lat: supplier?.lat || 0,
      pickup_lng: supplier?.lng || 0,
      pickup_contact: supplier?.contact || (supplier ? f.pickup_contact : ''),
    }));
  };

  const handleDropSupplier = (supplier) => {
    setForm(f => ({
      ...f,
      drop_supplier_name: supplier?.name || '',
      drop_location: supplier?.address || '',
      drop_lat: supplier?.lat || 0,
      drop_lng: supplier?.lng || 0,
    }));
  };

  const handleSubmit = async (e, { viaPickup = false, pickupUser = null } = {}) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!form.vehicle_type_1?.trim()) {
      alert('Vehicle Type Option 1 is mandatory.');
      return;
    }
    // Phase 68: GPS is mandatory on BOTH pickup and drop suppliers before booking.
    // If a supplier has no lat/lng in master, booker must tap Set Location first.
    const pickupSup = suppliers.find(s => s.name === form.pickup_supplier_name);
    const dropSup = suppliers.find(s => s.name === form.drop_supplier_name);
    const pickupHasGps = !!(pickupSup?.lat && pickupSup?.lng);
    const dropHasGps = !!(dropSup?.lat && dropSup?.lng);
    if (!pickupHasGps || !dropHasGps) {
      const missing = [];
      if (!pickupHasGps) missing.push('Pickup');
      if (!dropHasGps) missing.push('Drop');
      alert(`GPS location is missing for: ${missing.join(' & ')}.\nTap "Set Location" next to the supplier to pin it on the map before booking.`);
      return;
    }
    if (viaPickup && !pickupUser) {
      alert('Please select a Pickup user first.');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        pickup_flow: viaPickup,
        pickup_user_id: viaPickup ? pickupUser.id : '',
      };
      const { data: trip } = await api.post('/booking/trips', payload);
      // Phase 73 — Trip Pooling: if backend merged this booking into an existing
      // live trip, show the pool message before bouncing to the list.
      if (trip?.pooled) {
        alert(trip.message || `Your trip has been pooled with existing trip #${trip.pooled_into}. You don't need to book a separate vehicle.`);
        navigate('/booking');
        return;
      }
      // Pickup-flow trips skip allocation — bounce straight to list
      if (viaPickup) { navigate('/booking'); return; }
      // Check if preferred pilot was unavailable — show available pilots
      if (trip.preferred_pilot_unavailable && trip.status === 'pending') {
        setAvailPilotsLoading(true);
        try {
          const { data: pilots_list } = await api.get(`/trips/${trip.id}/available-pilots`);
          setPilotPickDialog({
            tripId: trip.id,
            pilots: pilots_list,
            tripInfo: `${trip.pickup_supplier_name} → ${trip.drop_supplier_name}`,
            // Phase 77a — show specific reason in the dialog header.
            reason: trip.preferred_pilot_manual_mode
              ? 'manual_mode'
              : (trip.preferred_pilot_not_found ? 'not_found' : 'unavailable'),
            preferredName: trip.preferred_pilot_name || '',
          });
        } catch {
          setPilotPickDialog({
            tripId: trip.id, pilots: [],
            tripInfo: `${trip.pickup_supplier_name} → ${trip.drop_supplier_name}`,
            reason: trip.preferred_pilot_manual_mode ? 'manual_mode' : (trip.preferred_pilot_not_found ? 'not_found' : 'unavailable'),
            preferredName: trip.preferred_pilot_name || '',
          });
        } finally { setAvailPilotsLoading(false); }
      } else {
        navigate('/booking');
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Booking failed');
    } finally {
      setLoading(false);
    }
  };

  const openPickupDialog = async () => {
    if (!form.pickup_supplier_name) {
      alert('Please select the Pickup Supplier first.');
      return;
    }
    try {
      const { data } = await api.get(`/booking/pickup-users?supplier=${encodeURIComponent(form.pickup_supplier_name)}`);
      if (!data || data.length === 0) {
        alert(`No Pickup users are set up for supplier "${form.pickup_supplier_name}". Ask the admin to add one from Pickup Users master.`);
        return;
      }
      setSelectedPickupUser(data.length === 1 ? data[0] : null);
      setPickupDialog({ candidates: data });
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to load Pickup users');
    }
  };

  const confirmPickupBooking = async () => {
    if (!selectedPickupUser) return;
    setPickupDialog(null);
    await handleSubmit(null, { viaPickup: true, pickupUser: selectedPickupUser });
  };

  const handlePickPilot = async (pilotId) => {
    if (!pilotPickDialog) return;
    try {
      await api.put(`/trips/${pilotPickDialog.tripId}/allocate-by-booking`, { pilot_id: pilotId });
      navigate('/booking');
    } catch (err) {
      alert(err.response?.data?.detail || 'Allocation failed');
    }
  };

  const handleManualRequest = () => {
    // Leave trip as pending for admin manual allocation
    navigate('/booking');
  };

  return (
    <div data-testid="new-booking" className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-[#002FA7] flex items-center justify-center"><Plus size={20} weight="bold" className="text-white" /></div>
        <div>
          <h1 className="text-2xl font-black tracking-tight text-[#0A0A0A]">Book Vehicle</h1>
          <p className="text-[#666] text-xs uppercase tracking-widest">New Trip Booking</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* SECTION 1: Pickup & Drop */}
        <div className="bg-white border border-[#E5E5E5] p-4 space-y-4">
          <h3 className="text-xs font-bold uppercase tracking-widest text-[#666] border-b border-[#E5E5E5] pb-2">Route</h3>
          
          {/* Pickup */}
          <div className="flex gap-3">
            <div className="flex flex-col items-center pt-2">
              <div className="w-3 h-3 rounded-full bg-[#00C853] ring-2 ring-[#00C853]/20" />
              <div className="w-0.5 flex-1 bg-[#E5E5E5] my-1" />
              <div className="w-3 h-3 rounded-full bg-[#FF3333] ring-2 ring-[#FF3333]/20" />
            </div>
            <div className="flex-1 space-y-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-[#00C853] font-bold">Pickup Supplier *</Label>
                <SupplierSearchSelect
                  suppliers={suppliers}
                  value={form.pickup_supplier_name}
                  onSelect={handlePickupSupplier}
                  placeholder="Search & select from master"
                  color="#00C853"
                  required
                  testIdPrefix="pickup-supplier"
                />
                {form.pickup_location && <p className="text-[10px] text-[#666] mt-1 truncate">{form.pickup_location}</p>}
                {(() => {
                  const sup = suppliers.find(s => s.name === form.pickup_supplier_name);
                  if (!sup) return null;
                  const hasGps = sup.lat && sup.lng;
                  return (
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      {hasGps ? (
                        <span className="text-[9px] uppercase tracking-widest font-bold text-[#00C853] bg-[#00C853]/10 px-1.5 py-0.5 rounded" data-testid="pickup-gps-ok">✓ GPS</span>
                      ) : (
                        <span className="text-[9px] uppercase tracking-widest font-bold text-[#FF3333] bg-[#FF3333]/10 px-1.5 py-0.5 rounded animate-pulse" data-testid="pickup-gps-missing">● No GPS</span>
                      )}
                      <button
                        type="button"
                        onClick={() => setLocDialog({ side: 'pickup', supplier: sup })}
                        className="text-[10px] font-bold uppercase tracking-widest text-[#002FA7] hover:underline flex items-center gap-1"
                        data-testid="pickup-set-location-btn"
                      >
                        <MapPin size={11} weight="fill" /> {hasGps ? 'Edit Location' : 'Set Location'}
                      </button>
                    </div>
                  );
                })()}
              </div>
              <Input value={form.pickup_contact} onChange={e => setForm({...form, pickup_contact: e.target.value})} className="rounded-lg text-sm" placeholder="Contact number (optional)" />
              
              <div className="pt-1">
                <Label className="text-[10px] uppercase tracking-widest text-[#FF3333] font-bold">Drop Supplier *</Label>
                <SupplierSearchSelect
                  suppliers={suppliers}
                  value={form.drop_supplier_name}
                  onSelect={handleDropSupplier}
                  placeholder="Search & select from master"
                  color="#FF3333"
                  required
                  testIdPrefix="drop-supplier"
                />
                {form.drop_location && <p className="text-[10px] text-[#666] mt-1 truncate">{form.drop_location}</p>}
                {(() => {
                  const sup = suppliers.find(s => s.name === form.drop_supplier_name);
                  if (!sup) return null;
                  const hasGps = sup.lat && sup.lng;
                  return (
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      {hasGps ? (
                        <span className="text-[9px] uppercase tracking-widest font-bold text-[#00C853] bg-[#00C853]/10 px-1.5 py-0.5 rounded" data-testid="drop-gps-ok">✓ GPS</span>
                      ) : (
                        <span className="text-[9px] uppercase tracking-widest font-bold text-[#FF3333] bg-[#FF3333]/10 px-1.5 py-0.5 rounded animate-pulse" data-testid="drop-gps-missing">● No GPS</span>
                      )}
                      <button
                        type="button"
                        onClick={() => setLocDialog({ side: 'drop', supplier: sup })}
                        className="text-[10px] font-bold uppercase tracking-widest text-[#002FA7] hover:underline flex items-center gap-1"
                        data-testid="drop-set-location-btn"
                      >
                        <MapPin size={11} weight="fill" /> {hasGps ? 'Edit Location' : 'Set Location'}
                      </button>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 2: Item & Quantity */}
        <div className="bg-white border border-[#E5E5E5] p-4 space-y-3">
          <h3 className="text-xs font-bold uppercase tracking-widest text-[#666] border-b border-[#E5E5E5] pb-2">Item Details</h3>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Item *</Label>
              <Input value={form.item} onChange={e => setForm({...form, item: e.target.value})} className="rounded-lg" list="item-list" required placeholder="Select" />
              <datalist id="item-list">{items.map(i => <option key={i.id} value={i.name} />)}</datalist>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Qty *</Label>
              <Input type="number" value={form.qty} onChange={e => setForm({...form, qty: parseFloat(e.target.value)||0})} className="rounded-lg" required />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest">UOM *</Label>
              <Input value={form.uom} onChange={e => setForm({...form, uom: e.target.value})} className="rounded-lg" list="uom-list" required placeholder="Kg/Pcs" />
              <datalist id="uom-list">{uoms.map(u => <option key={u.id||u.name} value={u.name} />)}</datalist>
            </div>
          </div>
        </div>

        {/* SECTION 3: Priority & Vehicle */}
        <div className="bg-white border border-[#E5E5E5] p-4 space-y-3">
          <h3 className="text-xs font-bold uppercase tracking-widest text-[#666] border-b border-[#E5E5E5] pb-2">Priority & Vehicle</h3>
          
          {/* Priority as big tap buttons */}
          <Label className="text-[10px] uppercase tracking-widest">Priority *</Label>
          <div className="grid grid-cols-4 gap-2">
            {PRIORITIES.map(p => (
              <button
                key={p.value}
                type="button"
                onClick={() => setForm({...form, priority: p.value})}
                className={`py-2.5 text-xs uppercase tracking-widest font-medium border transition-all ${
                  form.priority === p.value
                    ? p.value === 'shortage' ? 'bg-[#FF3333] text-white border-[#FF3333]'
                      : 'bg-[#002FA7] text-white border-[#002FA7]'
                    : 'bg-white text-[#666] border-[#E5E5E5] hover:border-[#002FA7]/40'
                }`}
                data-testid={`priority-${p.value}`}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Vehicle Type — Option 1 *</Label>
              <Input
                required
                value={form.vehicle_type_1}
                onChange={e => setForm({...form, vehicle_type_1: e.target.value})}
                className="rounded-full"
                list="vtype-list"
                placeholder="Mandatory"
                data-testid="vehicle-type-1"
              />
              <datalist id="vtype-list">{vehicleTypes.map(t => <option key={t} value={t} />)}</datalist>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Vehicle Type — Option 2</Label>
              <Input
                value={form.vehicle_type_2}
                onChange={e => setForm({...form, vehicle_type_2: e.target.value})}
                className="rounded-full"
                list="vtype-list"
                placeholder="Fallback (optional)"
                data-testid="vehicle-type-2"
              />
            </div>
          </div>

          {canSelectPreferred ? (
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Preferred Pilot</Label>
              <Input data-testid="preferred-pilot-input" value={form.preferred_pilot_name} onChange={e => setForm({...form, preferred_pilot_name: e.target.value})} className="rounded-lg" list="pilot-list" placeholder="Optional" />
              {/* Phase 77b — flag M-mode pilots in the datalist; browsers
                  don't render rich content here, so we suffix "(MANUAL)". */}
              <datalist id="pilot-list">{pilots.map(p => <option key={p.id} value={p.name}>{p.name} ({p.emp_id}){(p.allocation_mode || 'auto') === 'manual' ? ' — MANUAL ONLY' : ''}</option>)}</datalist>
              {/* Inline warning if the typed name is a manual-only pilot. */}
              {(() => {
                const matched = pilots.find(p => p.name?.toLowerCase() === (form.preferred_pilot_name || '').toLowerCase());
                if (matched && (matched.allocation_mode || 'auto') === 'manual') {
                  return (
                    <p className="text-[10px] text-[#FF8C00] mt-1" data-testid="preferred-pilot-manual-warning">
                      ⚠ {matched.name} is in <b>manual allocation only</b> mode. Auto-allocation will NOT go to this pilot — admin must assign manually.
                    </p>
                  );
                }
                return null;
              })()}
            </div>
          ) : (
            <div className="flex flex-col" data-testid="preferred-pilot-disabled-note">
              <Label className="text-[10px] uppercase tracking-widest text-[#999]">Preferred Pilot</Label>
              <p className="text-xs text-[#666] mt-1">Auto-allocation will choose the best pilot for this trip.</p>
            </div>
          )}

          <div>
            <Label className="text-[10px] uppercase tracking-widest">Material Ready</Label>
            <Input type="datetime-local" value={form.material_ready_datetime} onChange={e => setForm({...form, material_ready_datetime: e.target.value})} className="rounded-lg" />
          </div>
        </div>

        {/* SECTION 4: Remarks */}
        <div className="bg-white border border-[#E5E5E5] p-4">
          <Label className="text-[10px] uppercase tracking-widest">Remarks</Label>
          <textarea value={form.remarks} onChange={e => setForm({...form, remarks: e.target.value})} className="w-full border border-[#E5E5E5] p-3 text-sm min-h-[60px] mt-1" placeholder="Any special instructions..." />
        </div>

        {/* Submit row: Book via Pickup (left) + Book Vehicle (right) */}
        {(() => {
          const pickupSup = suppliers.find(s => s.name === form.pickup_supplier_name);
          const dropSup = suppliers.find(s => s.name === form.drop_supplier_name);
          const pickupHasGps = !!(pickupSup?.lat && pickupSup?.lng);
          const dropHasGps = !!(dropSup?.lat && dropSup?.lng);
          const gpsReady = (!!pickupSup && !!dropSup && pickupHasGps && dropHasGps);
          const missingList = [];
          if (pickupSup && !pickupHasGps) missingList.push('Pickup');
          if (dropSup && !dropHasGps) missingList.push('Drop');

          return (
            <>
              {missingList.length > 0 && (
                <div
                  className="border border-[#FF3333]/40 bg-[#FF3333]/10 text-[#FF3333] px-3 py-2 text-xs font-bold uppercase tracking-widest flex items-center gap-2"
                  data-testid="booking-gps-required-warning"
                >
                  <MapPin size={14} weight="fill" />
                  <span>GPS missing for {missingList.join(' & ')} — tap "Set Location" above to pin it before booking.</span>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Button
                  data-testid="book-via-pickup-btn"
                  type="button"
                  onClick={openPickupDialog}
                  disabled={loading || !gpsReady}
                  className="h-14 bg-[#8E44AD] text-white hover:bg-[#8E44AD]/90 hover:text-white rounded-full text-sm uppercase tracking-widest font-bold disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-[#AAAAAA] disabled:hover:bg-[#AAAAAA]"
                  title={
                    !pickupSup || !dropSup
                      ? 'Select Pickup & Drop suppliers first'
                      : !gpsReady
                      ? `Set GPS location for: ${missingList.join(' & ')}`
                      : 'Send to Pickup user for DC confirmation before allocation'
                  }
                >
                  Book via Pickup
                </Button>
                <Button
                  data-testid="submit-booking-btn"
                  type="submit"
                  disabled={loading || !gpsReady}
                  className="h-14 bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-full text-sm uppercase tracking-widest font-bold disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-[#AAAAAA] disabled:hover:bg-[#AAAAAA]"
                  title={
                    !pickupSup || !dropSup
                      ? 'Select Pickup & Drop suppliers first'
                      : !gpsReady
                      ? `Set GPS location for: ${missingList.join(' & ')}`
                      : 'Book now'
                  }
                >
                  {loading ? 'Booking...' : 'Book Vehicle'}
                </Button>
              </div>
            </>
          );
        })()}
      </form>

      {/* Pickup User Picker Dialog */}
      <Dialog open={!!pickupDialog} onOpenChange={(o) => { if (!o) { setPickupDialog(null); setSelectedPickupUser(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Select Pickup User</DialogTitle>
            <p className="text-sm text-[#666] mt-1">{form.pickup_supplier_name}</p>
            <p className="text-xs text-[#666]">Trip will wait at this Pickup user until they attach DC + mark goods ready.</p>
          </DialogHeader>
          <div className="space-y-2 mt-4 max-h-[50vh] overflow-y-auto" data-testid="pickup-user-candidates">
            {(pickupDialog?.candidates || []).map(u => (
              <button
                key={u.id}
                onClick={() => setSelectedPickupUser(u)}
                className={`w-full text-left p-3 border-2 transition-colors ${selectedPickupUser?.id === u.id ? 'border-[#8E44AD] bg-[#8E44AD]/5' : 'border-[#E5E5E5] hover:border-[#002FA7]/30'}`}
                data-testid={`pickup-user-option-${u.id}`}
              >
                <p className="text-sm font-semibold">{u.name}</p>
                <p className="text-xs text-[#666] font-mono">{u.mobile}</p>
              </button>
            ))}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => { setPickupDialog(null); setSelectedPickupUser(null); }} className="rounded-full">Cancel</Button>
            <Button
              onClick={confirmPickupBooking}
              disabled={!selectedPickupUser || loading}
              className="bg-[#8E44AD] text-white hover:bg-[#8E44AD]/90 hover:text-white rounded-full"
              data-testid="confirm-pickup-booking-btn"
            >
              Confirm Booking
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Available Pilots Dialog — when preferred pilot unavailable */}
      <Dialog open={!!pilotPickDialog} onOpenChange={() => {}}>
        <DialogContent className="max-w-[600px] max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {pilotPickDialog?.reason === 'manual_mode'
                ? 'Preferred Pilot Is Manual-Only'
                : pilotPickDialog?.reason === 'not_found'
                ? 'Preferred Pilot Not Found'
                : 'Preferred Pilot Not Available'}
            </DialogTitle>
            <p className="text-sm text-[#666] mt-1">Trip: {pilotPickDialog?.tripInfo}</p>
            {pilotPickDialog?.reason === 'manual_mode' && pilotPickDialog?.preferredName ? (
              <p className="text-xs text-[#FF3333]">
                {pilotPickDialog.preferredName} is set to <b>manual allocation only</b>.
                Auto-allocation has been blocked. Please pick another pilot below, or leave the trip pending for admin to manually allocate.
              </p>
            ) : (
              <p className="text-xs text-[#FF8C00]">Select an available pilot or request manual allocation</p>
            )}
          </DialogHeader>
          <div className="overflow-auto flex-1 mt-3">
            {availPilotsLoading ? (
              <p className="text-center py-6 text-[#666] text-sm">Loading available pilots...</p>
            ) : pilotPickDialog?.pilots?.length > 0 ? (
              <Table>
                <TableHeader><TableRow className="bg-[#F3F3F3]">
                  <TableHead className="text-xs uppercase tracking-widest">Pilot</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Distance</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Active</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Vehicle</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest"></TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {pilotPickDialog.pilots.map((p, i) => (
                    <TableRow key={p.id} className={i % 2 ? 'bg-[#F3F3F3]/30' : ''}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User size={14} className="text-[#002FA7]" />
                          <div>
                            <p className="font-medium text-sm">{p.name}</p>
                            <p className="text-[10px] text-[#666]">{p.mobile}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{p.distance_km < 900 ? `${p.distance_km} km` : '-'}</TableCell>
                      <TableCell className="text-sm">{p.active_trips} trips</TableCell>
                      <TableCell className="text-xs">{p.vehicle_number || '-'}</TableCell>
                      <TableCell>
                        <button
                          onClick={() => handlePickPilot(p.id)}
                          className="px-3 py-1.5 bg-[#002FA7] text-white text-xs uppercase tracking-widest hover:bg-[#002FA7]/90"
                          data-testid={`pick-pilot-${p.id}`}
                        >
                          Select
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-center py-6 text-[#666] text-sm">No pilots available for auto-allocation</p>
            )}
          </div>
          <button
            onClick={handleManualRequest}
            className="w-full mt-3 py-3 border border-[#E5E5E5] text-sm font-medium uppercase tracking-widest hover:bg-[#F3F3F3] text-[#666]"
            data-testid="manual-allocation-btn"
          >
            Manual — Leave for Admin
          </button>
        </DialogContent>
      </Dialog>

      {/* Supplier Location Picker — opens when "Set/Edit Location" is tapped */}
      {locDialog && (
        <SupplierLocationPicker
          open={!!locDialog}
          onClose={() => setLocDialog(null)}
          supplier={locDialog.supplier}
          currentUserId={user?.id || ''}
          isAdmin={isAdmin}
          onSaved={handleLocationSaved}
        />
      )}
    </div>
  );
}
